'use client'

import { Heart, X, ShoppingCart } from 'lucide-react'
import { useState, useEffect, useRef } from 'react'
import { useWishlistStore } from '@/stores/wishlist'
import { useCartStore } from '@/stores/cart'
import { useCurrencyStore } from '@/stores/currency'
import { useProducts } from '@/hooks/useProducts'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'

export default function WishlistDropdown() {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  
  const { items, removeItem } = useWishlistStore()
  const addToCart = useCartStore(state => state.addItem)
  const getSymbol = useCurrencyStore(state => state.getSymbol)
  const convert = useCurrencyStore(state => state.convert)
  const { data: productsData } = useProducts()

  const wishlistProducts = productsData?.products?.filter(p => items.includes(p.id)) || []

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleAddToCart = (product: typeof wishlistProducts[0]) => {
    addToCart({
      product_id: product.id,
      title: product.title,
      image: product.images[0]?.url || '',
      price: product.pricing.current_price,
      original_price: product.pricing.original_price,
      vendor_name: product.vendor.name,
    })
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <Button 
        variant="ghost" 
        size="sm" 
        className="relative"
        onClick={() => setIsOpen(!isOpen)}
      >
        <Heart className="h-5 w-5" />
        {items.length > 0 && (
          <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-accent text-accent-foreground text-xs flex items-center justify-center">
            {items.length}
          </span>
        )}
      </Button>

      {isOpen && (
        <Card className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto shadow-lg z-50 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-muted [&::-webkit-scrollbar-thumb]:bg-muted-foreground/30 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-muted-foreground/50">
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg">Wishlist</h3>
              <button onClick={() => setIsOpen(false)} className="text-muted-foreground hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            </div>

            {wishlistProducts.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Heart className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Your wishlist is empty</p>
              </div>
            ) : (
              <div className="space-y-3">
                {wishlistProducts.map((product) => (
                  <div key={product.id} className="flex gap-3 pb-3 border-b border-border">
                    <img 
                      src={product.images[0]?.url} 
                      alt={product.title} 
                      className="w-16 h-16 object-cover rounded"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium line-clamp-2">{product.title}</p>
                      <p className="text-sm text-brand-orange font-semibold">
                        {getSymbol()}{convert(product.pricing.current_price).toLocaleString()}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <button
                          onClick={() => handleAddToCart(product)}
                          className="text-xs text-primary hover:underline flex items-center gap-1"
                        >
                          <ShoppingCart className="h-3 w-3" />
                          Add to Cart
                        </button>
                        <button
                          onClick={() => removeItem(product.id)}
                          className="ml-auto text-destructive text-xs hover:underline"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  )
}
